import { useState, useRef, useEffect } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import {
  getPrevNextKey,
  getFirstVisibleKey,
  getKey,
  keyExtractor
} from '../utils/PRIVATE_collectionUtils';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useId } from '../hooks/UNSAFE_useId';

import { ComponentProps, ContextType, RefObject } from 'preact';
import type { TabBarContext, TabBarLayout } from '../UNSAFE_TabBarCommon';

type TabBarContextValue = ContextType<typeof TabBarContext>;

export type UseTabBarOptions = {
  class?: string;
  display?: TabBarContextValue['display'];
  edge?: 'bottom' | 'top';
  onRemove?: TabBarContextValue['onRemove'];
  onSelect?: TabBarContextValue['onSelect'];
  ref?: RefObject<HTMLDivElement>;
  selection?: TabBarContextValue['selection'];
  size?: TabBarContextValue['size'];
  children?: ComponentProps<typeof TabBarLayout>['children'];
};

const ITEM_SELECTOR = '[role="tab"]';
const REMOVAL_ITEM_SELECTOR = '[data-oj-tabbar-item-remove-icon]';

/**
 * Re-implements UNSAFE_TabBar/TabBar behavior for focus and keyboad handling
 */
export function useTabBar(options: UseTabBarOptions) {
  const {
    children,
    class: className,
    display,
    edge = 'top',
    selection,
    onSelect,
    onRemove,
    ref: rootRef = useRef<HTMLDivElement>(null),
    size
  } = options;

  const tabId = useId();
  const [currentKey, setCurrentKey] = useState(selection);

  const tabItemPrefix = tabId + '_';

  /**
   * A reference to the previous set of tab keys that this tablist
   * contains before children were re-rendered. In the event the "current" tab
   * is removed and the "currentKey" reference is broken, we can redirect
   * the "currentKey" to another tab based on the index of the removed tab.
   */
  const prevTabKeys = useRef<(string | number | undefined)[]>();

  useEffect(() => {
    if (rootRef.current) {
      const tabKeys = Array.from(rootRef.current.querySelectorAll(ITEM_SELECTOR)).map((elem) =>
        getKey(elem as HTMLElement)
      );

      if (currentKey && !tabKeys.includes(currentKey) && prevTabKeys.current) {
        setCurrentKey(findNextCurrentKey(currentKey, tabKeys, prevTabKeys.current));
      }
      prevTabKeys.current = tabKeys;
    }
  }, [children, currentKey]);

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
    'ArrowRight',
    'ArrowLeft'
  ]);

  const { currentKeyProps } = useCurrentKey(
    (element) =>
      onRemove
        ? extractOnlyItemKey(element, ITEM_SELECTOR, REMOVAL_ITEM_SELECTOR)
        : keyExtractor(element, ITEM_SELECTOR),
    false,
    undefined,
    undefined,
    getPrevNextKey(rootRef.current, currentKey, true, ITEM_SELECTOR),
    getPrevNextKey(rootRef.current, currentKey, false, ITEM_SELECTOR),
    currentKey,
    (detail) => setCurrentKey(detail.value)
  );

  const onKeyDown = (event: KeyboardEvent) => {
    if (currentKey && event.key === 'Enter') {
      onSelect?.({ value: currentKey });
    }
    if (currentKey && event.key === 'Delete') {
      onRemove?.({ value: currentKey });
    }
  };

  const onFocus = () => {
    if (rootRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(rootRef.current, ITEM_SELECTOR);
      if (key) {
        setCurrentKey(key);
      }
    }
  };

  const eventProps = { onKeyDown, onFocus };

  return {
    rootProps: mergeProps(
      {
        'aria-activedescendant': currentKey ? tabItemPrefix + currentKey : '',
        'aria-multiselectable': false,
        class: className,
        ref: rootRef,
        role: 'tablist',
        tabIndex: 0
      },
      currentKeyProps,
      focusRingProps,
      eventProps
    ),
    tabBarContext: {
      currentKey,
      display,
      isEdgeBottom: edge === 'bottom',
      layout: 'condense',
      onRemove,
      onSelect,
      showFocusRing,
      selection,
      size,
      tabItemPrefix
    }
  } as const;
}

//useCurrentKey use click capture that captures first click, which is on remove button
//when we remove and sets the item being removed to currentKey. So we should not allow
//this if click is on remove button
const extractOnlyItemKey = (element: HTMLElement, itemSelector: string, itemEliminator: string) => {
  const tabBarItem = element.closest(itemSelector);
  const removeButton = element.closest(itemEliminator);
  return tabBarItem?.contains(removeButton) ? null : keyExtractor(element, itemSelector);
};

const findNextCurrentKey = <K>(currentKey: K, currTabs: K[], prevTabs: K[]) => {
  const index = prevTabs.indexOf(currentKey);
  if (index > 0) {
    const nextIndex = index === prevTabs.length - 1 ? index - 1 : index + 1;
    const nextKey = prevTabs[nextIndex];
    if (currTabs.indexOf(nextKey) !== -1) {
      return nextKey;
    }
  }
  // update current key to be the first one if we can't find a suitable next key
  return currTabs[0];
};
